import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Sparkles } from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";

/**
 * Clean post-Stripe-Checkout success page. No session tokens in URL.
 * Calls /api/business/billing/sync to confirm the payment (covers webhook
 * delays), then polls /api/business/billing until status flips to active.
 */
export default function BillingSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("syncing"); // syncing | active | timeout

  useHideEmergentBadge();

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 30;
    let cancelled = false;

    const run = async () => {
      try { await api.post("/business/billing/sync"); } catch {/* webhook may have already run */}
      const poll = async () => {
        if (cancelled) return;
        attempts += 1;
        try {
          const { data } = await api.get("/business/billing");
          if (data && (data.status === "active" || data.status === "manual_bypass")) {
            setStatus("active");
            return;
          }
        } catch { /* keep polling */ }
        if (attempts >= maxAttempts) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      };
      poll();
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb] p-6" data-testid="billing-success-page">
      <div className="bg-white border border-[#e5e5eb] rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-sm">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-3">
          <Sparkles className="w-3 h-3" /> SparkLoop
        </div>
        {status === "active" ? (
          <>
            <div className="w-16 h-16 rounded-full bg-[#dcf6e2] text-[#1a6b34] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight mb-3">
              You're all set.
            </h1>
            <p className="text-[#5a5a65] mb-6 leading-relaxed">
              Your subscription is active. Your kiosks and rewards keep running, uninterrupted.
            </p>
            <button
              data-testid="billing-success-continue-btn"
              onClick={() => navigate("/merchant", { replace: true })}
              className="w-full h-14 rounded-2xl bg-[#002fa7] hover:bg-[#002585] text-white font-bold text-base"
            >
              Back to dashboard
            </button>
          </>
        ) : status === "timeout" ? (
          <>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter leading-tight mb-3">
              Still processing…
            </h1>
            <p className="text-[#5a5a65] mb-6 leading-relaxed text-sm">
              Stripe is taking a moment to confirm your payment. Refresh this page in a minute, or head to the dashboard — we'll mark it active as soon as the confirmation arrives.
            </p>
            <button
              data-testid="billing-timeout-continue-btn"
              onClick={() => navigate("/merchant", { replace: true })}
              className="w-full h-14 rounded-2xl bg-[#002fa7] hover:bg-[#002585] text-white font-bold text-base"
            >
              Go to dashboard
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter leading-tight mb-3">
              Confirming your payment…
            </h1>
            <p className="text-[#5a5a65] text-sm">This usually takes a few seconds.</p>
            <div className="mt-6 inline-block w-8 h-8 border-4 border-[#e5e5eb] border-t-[#002fa7] rounded-full animate-spin" />
          </>
        )}
      </div>
    </div>
  );
}
